const { CURRENCIES } = require('./payment');

const CONSTANTS = (module.exports = {});

// Unified append-only ledger transaction types.
// `amount` is always a POSITIVE magnitude; direction is derived from the type.
CONSTANTS.CASH_WALLET_TRANSACTION_TYPE = {
    COMMISSION_CREDIT: 'COMMISSION_CREDIT',
    COMMISSION_ADJUSTMENT_CREDIT: 'COMMISSION_ADJUSTMENT_CREDIT',
    WITHDRAWAL_DEBIT: 'WITHDRAWAL_DEBIT',
    // System-Admin wallet-creation opening balance (AR-3050/3055). Never covered by the
    // (cash_wallet_id, reservation_id) partial-unique idempotency index — there is no reservation_id.
    OPENING_BALANCE_CREDIT: 'OPENING_BALANCE_CREDIT',
};

// Ledger types that increase / decrease the stored balance.
// balance = Σ(credit types) − Σ(debit types).
CONSTANTS.CASH_WALLET_CREDIT_TYPES = [
    CONSTANTS.CASH_WALLET_TRANSACTION_TYPE.COMMISSION_CREDIT,
    CONSTANTS.CASH_WALLET_TRANSACTION_TYPE.COMMISSION_ADJUSTMENT_CREDIT,
    CONSTANTS.CASH_WALLET_TRANSACTION_TYPE.OPENING_BALANCE_CREDIT,
];

CONSTANTS.CASH_WALLET_DEBIT_TYPES = [CONSTANTS.CASH_WALLET_TRANSACTION_TYPE.WITHDRAWAL_DEBIT];

// Withdrawal request status machine — single linear flow:
// Submitted → In Review → Paid | Rejected. Both Finance terminal states only from In Review.
// Cancelled is a terminal, AGENT-initiated exit allowed ONLY from Submitted (before Finance picks it up).
CONSTANTS.WITHDRAWAL_REQUEST_STATUS = {
    SUBMITTED: 'Submitted',
    IN_REVIEW: 'In Review',
    PAID: 'Paid',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
};

// Statuses for which a request is considered "open" (single-active guard).
// Cancelled is terminal, so it is NOT open — cancelling frees the single-active partial-unique index.
CONSTANTS.WITHDRAWAL_REQUEST_OPEN_STATUSES = [
    CONSTANTS.WITHDRAWAL_REQUEST_STATUS.SUBMITTED,
    CONSTANTS.WITHDRAWAL_REQUEST_STATUS.IN_REVIEW,
];

// Statuses that consume the agent's one-request-per-calendar-month allowance. Deliberately WIDER than
// WITHDRAWAL_REQUEST_OPEN_STATUSES: a Paid request is closed but still used up the month. Rejected and
// Cancelled do NOT count — the agent keeps the allowance and may re-submit within the same month.
CONSTANTS.WITHDRAWAL_MONTHLY_CAP_QUALIFYING_STATUSES = [
    CONSTANTS.WITHDRAWAL_REQUEST_STATUS.SUBMITTED,
    CONSTANTS.WITHDRAWAL_REQUEST_STATUS.IN_REVIEW,
    CONSTANTS.WITHDRAWAL_REQUEST_STATUS.PAID,
];

// Machine-readable rejection codes for the agent cancel endpoint (AR-3041). Two distinct 409 codes so the
// client can branch on the code (message is display-only): a request under Finance review vs. an already
// terminal (Paid/Rejected/Cancelled) request.
CONSTANTS.WITHDRAWAL_CANCEL_ERROR = {
    ALREADY_IN_REVIEW: 'ALREADY_IN_REVIEW',
    ALREADY_RESOLVED: 'ALREADY_RESOLVED',
};

CONSTANTS.WITHDRAWAL_CANCEL_MESSAGE = {
    ALREADY_IN_REVIEW: 'This request is already being reviewed and can no longer be cancelled.',
    ALREADY_RESOLVED: 'This request has already been resolved and can no longer be cancelled.',
};

// Shared copy for every Finance/ADMIN transition blocked by the GLOBAL payout freeze (AR-3068/3069, C4).
CONSTANTS.CASH_WALLET_PAYOUT_FROZEN_MESSAGE = 'Payouts are currently frozen. Please try again later.';

// Open ⇒ In Review (Finance "lock") transition error codes (AR-3041).
CONSTANTS.WITHDRAWAL_OPEN_ERROR = {
    NOT_SUBMITTED: 'NOT_SUBMITTED',
    FROZEN: 'FROZEN',
};

CONSTANTS.WITHDRAWAL_OPEN_MESSAGE = {
    NOT_SUBMITTED: 'This request is no longer in a submittable state and cannot be opened for review.',
    FROZEN: CONSTANTS.CASH_WALLET_PAYOUT_FROZEN_MESSAGE,
};

// Paid transition error codes (AR-3041/3052/3064). Marking Paid records the manual Finance transfer.
CONSTANTS.WITHDRAWAL_PAY_ERROR = {
    NOT_IN_REVIEW: 'NOT_IN_REVIEW',
    FROZEN: 'FROZEN',
    INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
    CURRENCY_MISMATCH: 'CURRENCY_MISMATCH',
};

CONSTANTS.WITHDRAWAL_PAY_MESSAGE = {
    NOT_IN_REVIEW: 'This request is not currently under review and cannot be marked as paid.',
    FROZEN: CONSTANTS.CASH_WALLET_PAYOUT_FROZEN_MESSAGE,
    INSUFFICIENT_BALANCE: 'The wallet balance is insufficient to cover this withdrawal.',
    // Defense-in-depth only — wallet currency is immutable (AR-3055) and request currency is a submit-time
    // snapshot, so this should never surface in practice.
    CURRENCY_MISMATCH: 'The request currency does not match the wallet currency. Contact engineering.',
};

// Rejected transition error codes (AR-3070). `rejection_comment` is mandatory only for `OTHER` (B4).
CONSTANTS.WITHDRAWAL_REJECT_ERROR = {
    INVALID_REASON: 'INVALID_REASON',
    COMMENT_REQUIRED: 'COMMENT_REQUIRED',
    NOT_IN_REVIEW: 'NOT_IN_REVIEW',
    FROZEN: 'FROZEN',
};

CONSTANTS.WITHDRAWAL_REJECT_MESSAGE = {
    INVALID_REASON: 'Please select a valid rejection reason.',
    COMMENT_REQUIRED: 'A comment is required when the rejection reason is "Other".',
    NOT_IN_REVIEW: 'This request is not currently under review and cannot be rejected.',
    FROZEN: CONSTANTS.CASH_WALLET_PAYOUT_FROZEN_MESSAGE,
};

// Global payout-freeze toggle (AR-3068/3069). Fixed key identifying the single settings singleton doc —
// a unique index on this field makes the C4 "upsert-touch under an enabled:false guard" race-safe: only
// ONE document can ever exist, so a concurrent first-toggle vs first-payout can never create two singletons.
CONSTANTS.CASH_WALLET_PAYOUT_FREEZE_SINGLETON_KEY = 'GLOBAL_PAYOUT_FREEZE';

CONSTANTS.CASH_WALLET_FREEZE_TOGGLE_ERROR = {
    INVALID_ENABLED: 'INVALID_ENABLED',
};

CONSTANTS.CASH_WALLET_FREEZE_TOGGLE_MESSAGE = {
    INVALID_ENABLED: '"enabled" must be a boolean.',
};

// Tax classification. `None` blocks submission (AR-3057); only
// `Authorised`/`Exempt` are ever persisted on a submitted request.
CONSTANTS.WITHDRAWAL_TAX_CLASSIFICATION = {
    NONE: 'None',
    AUTHORISED: 'Authorised',
    EXEMPT: 'Exempt',
};

// Rejection reasons (AR-3070). `rejection_comment` is mandatory only for `OTHER` (B4).
CONSTANTS.WITHDRAWAL_REJECTION_REASON = {
    INVALID_INVOICE: 'Invoice Invalid',
    AMOUNT_MISMATCH: "Amount Doesn't Match",
    INVOICE_MISSING_DETAILS: 'Invoice Missing Required Details',
    INCORRECT_BILLING_ENTITY: 'Incorrect Billing Entity',
    DUPLICATE_REQUEST: 'Duplicate Request',
    TAX_CLASSIFICATION_MISMATCH: 'Tax Classification Mismatch',
    OTHER: 'Other',
};

// Currencies a Cash Wallet may be denominated in — set once, immutable (AR-3055).
// Reuses the shared CURRENCIES enum; ILS is the canonical shekel code (NIS normalizes to it).
CONSTANTS.CASH_WALLET_CURRENCIES = [CURRENCIES.USD, CURRENCIES.ILS];

// Reasons the nightly eligibility batch persists a skip record instead of crediting.
// Persisting the skip prevents re-evaluating/re-logging the same reservation on every run.
CONSTANTS.CASH_WALLET_COMMISSION_SKIP_REASON = {
    CURRENCY_MISMATCH: 'CURRENCY_MISMATCH',
};

// Withdrawal thresholds per wallet currency: $500 USD / ₪1,500 ILS.
// NET (ex-VAT) figures, on BOTH sides of the flow: the withdraw button compares them against
// `maxWithdrawableNet(balance, CASH_WALLET_VAT_RATE)` (never the stored GROSS balance), the submit handler
// against the submitted NET amount. Gross must therefore reach threshold × (1 + VAT_RATE) — e.g. $590.
CONSTANTS.CASH_WALLET_WITHDRAWAL_THRESHOLD = {
    [CURRENCIES.USD]: 500,
    [CURRENCIES.ILS]: 1500,
};

// Statutory VAT rate (18%). `vat` is derived from the submitted NET at THIS rate for BOTH tax
// classifications (see `deriveNetVat` in packages/utils/money.js) — the wallet carries the VAT liability
// from the original commission accrual however the agent is classified. Classification only decides the
// PAYOUT (gross for `Authorised`, net-only for `Exempt`); the wallet debit on Paid is ALWAYS net + vat.
CONSTANTS.CASH_WALLET_VAT_RATE = 0.18;

// Maximum withdrawal per wallet currency: $17,000 USD / ₪50,000 ILS (AR-3058).
// ⚠ THE BASIS IS THE PAYOUT TOTAL, NOT THE SUBMITTED NET — the opposite of
// CASH_WALLET_WITHDRAWAL_THRESHOLD above, which is a NET figure. The capped value is
// `deriveNetVat(...).amount`: GROSS (net + vat) for `Authorised`, NET-ONLY for `Exempt` — the same figure
// the drawer renders as "Total" / "You'll receive" and the invoice Total the ticket's acceptance criteria
// names. Reading this as a NET cap is a money bug in BOTH directions: it would let an Authorised agent
// past the maximum by 18% (a $17,000 net pays out $20,060), and it would reject an Exempt agent early.
CONSTANTS.CASH_WALLET_WITHDRAWAL_MAX_AMOUNT = {
    [CURRENCIES.USD]: 17000,
    [CURRENCIES.ILS]: 50000,
};

// Human-readable maximum, mirroring CASH_WALLET_THRESHOLD_DISPLAY. This is the copy the agent must see,
// pinned here so the rendered inline error cannot silently drift from the figure that is enforced.
CONSTANTS.CASH_WALLET_MAX_AMOUNT_DISPLAY = {
    [CURRENCIES.USD]: '$17,000',
    [CURRENCIES.ILS]: '₪50,000',
};

// The money-amount input pattern lives in packages/utils/money.js as `AMOUNT_RE` and is deliberately NOT
// re-exported here: `@arbitrip/utils` already depends on `@arbitrip/constants`, and this module is loaded
// by lambda layers and services that do not depend on `@arbitrip/utils` at all.

// Invoice upload constraints (AR-3060): PDF only, ≤ 5 MB.
// The content-type is matched by MIME prefix (parameters like `; charset=` allowed) AND the buffer must
// start with the PDF magic bytes — header trust alone is never sufficient (money-input hardening).
CONSTANTS.CASH_WALLET_INVOICE_CONTENT_TYPE = 'application/pdf';
CONSTANTS.CASH_WALLET_INVOICE_MAGIC_BYTES = '%PDF-';
CONSTANTS.CASH_WALLET_INVOICE_MAX_SIZE_BYTES = 5 * 1024 * 1024;

// `short_id` is the last 6 hex chars of the withdrawal request `_id` — DISPLAY-ONLY.
CONSTANTS.CASH_WALLET_SHORT_ID_LENGTH = 6;

// Human-readable threshold rendered inside the BELOW_THRESHOLD button copy (B4b).
CONSTANTS.CASH_WALLET_THRESHOLD_DISPLAY = {
    [CURRENCIES.USD]: '$500',
    [CURRENCIES.ILS]: '₪1,500',
};

// Dashboard withdraw-button reason codes, STRICT precedence:
// FROZEN → ACTIVE_REQUEST → MONTHLY_CAP_REACHED → BELOW_THRESHOLD → ZERO_BALANCE → ENABLED.
// FROZEN here is the GLOBAL payout freeze (AR-3068/3069).
CONSTANTS.WITHDRAWAL_BUTTON_STATE = {
    ENABLED: 'ENABLED',
    ZERO_BALANCE: 'ZERO_BALANCE',
    BELOW_THRESHOLD: 'BELOW_THRESHOLD',
    ACTIVE_REQUEST: 'ACTIVE_REQUEST',
    MONTHLY_CAP_REACHED: 'MONTHLY_CAP_REACHED',
    FROZEN: 'FROZEN',
};

// Copy strings per reason code. BELOW_THRESHOLD is currency-specific and built at read time
// from CASH_WALLET_THRESHOLD_DISPLAY via WITHDRAWAL_BUTTON_BELOW_THRESHOLD_TEMPLATE.
CONSTANTS.WITHDRAWAL_BUTTON_MESSAGE = {
    ENABLED: '',
    ZERO_BALANCE: 'To request withdrawal, you need to have available balance.',
    ACTIVE_REQUEST: 'You can submit only one request at a time.',
    MONTHLY_CAP_REACHED: 'You can only submit one withdrawal request per calendar month.',
    FROZEN: 'It is not possible to make a withdrawal at this time. Please try again later.',
};

CONSTANTS.WITHDRAWAL_BUTTON_BELOW_THRESHOLD_TEMPLATE =
    'To request a withdrawal, you need to have available balance above {threshold}.';

// Agent withdrawal-requests list pagination — 10 per page (AR-3048).
CONSTANTS.WITHDRAWAL_REQUESTS_PAGE_SIZE = 10;

// Finance (trader-api) withdrawal queue pagination — cross-agent, larger default page than the agent
// dashboard list, with a hard cap so an unbounded `page_size` query param can't force a full table scan.
CONSTANTS.CASH_WALLET_FINANCE_QUEUE_DEFAULT_PAGE_SIZE = 20;
CONSTANTS.CASH_WALLET_FINANCE_QUEUE_MAX_PAGE_SIZE = 100;

// Finance queue status filter — machine-readable error when an unrecognized `status` query param is
// supplied. Message is display-only (client branches on the code).
CONSTANTS.CASH_WALLET_FINANCE_QUEUE_ERROR = {
    INVALID_STATUS_FILTER: 'INVALID_STATUS_FILTER',
};

CONSTANTS.CASH_WALLET_FINANCE_QUEUE_MESSAGE = {
    INVALID_STATUS_FILTER: 'Unrecognized withdrawal request status filter.',
};

// Bill-to entity — FIXED, single hardcoded entity (AR-3059). Not env-configurable, not per-currency.
CONSTANTS.CASH_WALLET_BILL_TO = {
    name: 'Arigato Advanced Travel Technology LTD',
    phone: '+972 3 738 2100',
    email: 'support@arbitrip.com',
    address: 'Menachem Begin 132, Azrieli Triangle Tower, Tel Aviv, Israel, 6701101',
};

// System-Admin (ADMIN-only) wallet-lifecycle audit actions (AR-3050/3053/3054/3055) appended to
// `CashWallet.audit_log[]`. `CAN_WITHDRAW_ON`/`CAN_WITHDRAW_OFF`/`CURRENCY_SET` are lower-cased verbatim
// per the model's documented enum — everything else follows the CREATED/DEACTIVATED/REACTIVATED casing.
CONSTANTS.CASH_WALLET_ADMIN_AUDIT_ACTION = {
    CREATED: 'CREATED',
    DEACTIVATED: 'DEACTIVATED',
    REACTIVATED: 'REACTIVATED',
    CAN_WITHDRAW_ON: 'can_withdraw_on',
    CAN_WITHDRAW_OFF: 'can_withdraw_off',
    CURRENCY_SET: 'currency_set',
    // Opening-balance-at-creation audit entry (AR-3050/3055) — `detail` carries the amount set.
    OPENING_BALANCE_SET: 'opening_balance_set',
};

// System-Admin wallet-lifecycle endpoint error codes (create/deactivate/reactivate/can_withdraw/state).
CONSTANTS.CASH_WALLET_WALLET_ADMIN_ERROR = {
    INVALID_CURRENCY: 'INVALID_CURRENCY',
    WALLET_ALREADY_EXISTS: 'WALLET_ALREADY_EXISTS',
    WALLET_NOT_FOUND: 'WALLET_NOT_FOUND',
    INVALID_ENABLED: 'INVALID_ENABLED',
    INVALID_INITIAL_BALANCE: 'INVALID_INITIAL_BALANCE',
    AGENT_NOT_FOUND: 'AGENT_NOT_FOUND',
};

CONSTANTS.CASH_WALLET_WALLET_ADMIN_MESSAGE = {
    INVALID_CURRENCY: 'Unsupported wallet currency.',
    WALLET_ALREADY_EXISTS: 'A wallet already exists for this agent.',
    WALLET_NOT_FOUND: 'No wallet exists for this agent.',
    INVALID_ENABLED: '"enabled" must be a boolean.',
    INVALID_INITIAL_BALANCE: 'The initial balance must be a positive number with at most 2 decimal places.',
    AGENT_NOT_FOUND: 'No agent exists for this id.',
};
